import { useEffect, useState, useRef, useCallback } from 'react';
import { useTable } from 'react-final-table';
import isEqual from 'lodash.isequal';

// types
import { TableProps, Row } from './types';

// hooks
import useSelectedTags from './hooks/use-selected-tags';
import useHorizontalScroll from './hooks/use-horizontal-scroll';
import usePrevious from '../../hooks/use-previous';
import useWheelEvents from './hooks/use-wheel';

// components
import TagsList from './tags-list';
import CheckboxInput from '../checkbox/checkbox';
import TableSettings from './table-settings';
import IconLoader from '../icons/loader';
import IconAttention from '../icons/attention';
import TableRow from './table-row';
import TableHorizontalScroll from './table-horizontal-scroll';
import ButtonPseudo from '../button/button-pseudo';
import { ButtonSize } from '../button/types';
import IconLeft from '../icons/left';
import IconRight from '../icons/right';
import TableHeadItem from './table-head-item';
import Popup from '../popup/popup';
import InputText from '../inputs/input-text';
import IconClose from '../icons/close';
import SavedFilters from './saved-filters-component';
import IconSelected from '../icons/selected';
import IconBlock from '../icons/block';
import IconUpload from '../icons/upload';
import ButtonSecondary from '../button/button-secondary';
import ToggleInput from '../checkbox/toggle';
import IconRefresh from '../icons/refresh';

// styles
import './table.css';
import { SelectItemProps } from '../select/types';

export default function Table({
  columns,
  data,
  filters,
  sort,
  onSort,
  columnsLabels,
  title,
  onRowsSelect,
  filtersComponent,
  onFilterChange,
  handleSubmit,
  withSettings,
  storeKey,
  topContent,
  isLoading,
  expandable,
  expandableColumns,
  tableFormClassName = '',
  tableTheadClassName = '',
  tableHeadCellClassName = '',
  tableBodyClassName = '',
  tableClassName = '',
  tableTopClassName = '',
  withScroll = false,
  pagination,
  setPage,
  take,
  onExpand,
  isLoadingExpandableRow,
  error,
  paginationDisabled,
  disabledRowIds,
  clickable,
  linkWithoutSearch,
  onClearAutoComplete,
  onRowClick,
  collapseAllRows,
  withVerticalScroll,
  isFetching,
  hiddenFiltersTags,
  withDuplicateButton,
  onDuplicateClick,
  clearSelectedItems,
  transactionTable,
  fullWidthTable,
  savedFilters,
  savedFiltersKey,
  setSavedFilters,
  onSavedFilterClick,
  selectedSavedFilter,
  setSelectedSavedFilter,
  hiddedAllFilterTags,
  filtersContent,
  userId,
  tableControlsClassName = '',
  isSuccessExpandableRow,
  withSaveSelectedRows,
  withExpandableContent,
  expandableColspanValue,
  nestedTable,
  withToggleInput,
  withSelectAllRowsOnExpand,
  tableName,
  newWindow,
  rowsSelectDisabled,
  selectedIds,
  withDeleteIcon,
  onToggleAll,
}: TableProps): React.JSX.Element {
  const titleRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollWrapper = useRef<HTMLDivElement | null>(null);
  const tableScrollerRef = useRef<HTMLDivElement | null>(null);
  const falseScrollRightRef = useRef<HTMLDivElement | null>(null);
  const uploadFiltersPresetRef = useRef<HTMLInputElement | null>(null);
  const reuploadFiltersPresetRef = useRef<HTMLInputElement | null>(null);

  const [tableColumns, setTableColumns] = useState(columns);
  const [topContentHeight, setTopContentHeight] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [expandableColspan, setExpandableColspan] = useState(0);
  const [saveFilterPopupVisible, setSaveFilterPopupVisible] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [defaultFilter, setDefaultFilter] = useState(false);
  const [uploadErrorTextVisible, setUploadErrorTextVisible] = useState(false);
  const [uploadFilterSuccess, setUploadFilterSuccess] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [onWheelActive, setOnWheelActive] = useState(false);
  const [filtersLists, setFiltersList] =
    useState<Record<string, SelectItemProps[]>>();

  const { selectedOptionsTags } = useSelectedTags(filters, columnsLabels);

  const { rows } = useTable(tableColumns, data, {
    selectable: Boolean(onRowsSelect),
  });

  const prevRows = usePrevious(rows);
  const prevColumns = usePrevious(columns);
  const prevFetching = usePrevious(isFetching);

  const wheelHandlers = useWheelEvents({
    onWheelStart: () => setOnWheelActive(true),
    onWheelStop: () => setOnWheelActive(false),
    delay: 50,
  });

  const {
    onScrollButton,
    onWheel,
    onDrag,
    onDragEnd,
    scrollBarThumbWidth,
    scrollThumbPosition,
    accessibleDragPosition,
  } = useHorizontalScroll({
    tableRef,
    tableScrollWrapper,
    tableContainerRef,
    tableScrollerRef,
  });

  const selectRow = (row: Row) => {
    let nextSelectedRows = ([] as Row[]).concat(selectedRows);

    if (nextSelectedRows.find((item) => item.original.id === row.original.id)) {
      nextSelectedRows = nextSelectedRows.filter(
        (item) => item.original.id !== row.original.id,
      );
    } else {
      nextSelectedRows.push(row);
    }

    setSelectedRows(nextSelectedRows);

    if (onRowsSelect) {
      onRowsSelect(
        nextSelectedRows.map((item) => item.original.id),
        row?.original?.id,
      );
    }
  };

  const toggleAllWithSaveSelected = () => {
    let list: Row[] = [];

    if (disabledRowIds) {
      if (
        !rows
          ?.filter((item) =>
            disabledRowIds.every((id) => id !== item?.original?.id),
          )
          ?.map((item) => item.original.id)
          .every((item) =>
            selectedRows.map((rowItem) => rowItem.original.id).includes(item),
          )
      ) {
        list = rows?.filter((item) =>
          disabledRowIds?.every((id) => id !== item.original.id),
        ) as Row[];

        selectedRows?.forEach((item) => {
          if (
            !list.find((listItem) => listItem.original.id === item.original.id)
          ) {
            list.push(item);
          }
        });
      } else {
        list = selectedRows?.filter((item) =>
          rows.every((rowItem) => item.original.id !== rowItem.original.id),
        );
      }

      setSelectedRows(list);

      if (onRowsSelect) {
        onRowsSelect(list.map((item) => item.original.id));
      }
    }
  };

  const toggleAll = useCallback(
    (toggle: boolean) => {
      let list: Row[] = [];

      setAllSelected(toggle);

      if (toggle) {
        if (disabledRowIds?.length) {
          list = rows?.filter((item) =>
            disabledRowIds?.every((id) => id !== item.original.id),
          ) as Row[];

          if (withSaveSelectedRows) {
            selectedRows?.forEach((item) => {
              if (
                !list.find(
                  (listItem) => listItem.original.id === item.original.id,
                )
              ) {
                list.push(item);
              }
            });
          }
        } else {
          list = rows as Row[];

          if (withSaveSelectedRows) {
            list = ([] as Row[]).concat(selectedRows);

            rows.forEach((item) => {
              if (
                !list.find(
                  (listItem) => listItem.original.id === item.original.id,
                )
              ) {
                list.push(item as Row);
              }
            });
          }
        }
      }

      setSelectedRows(list);

      if (onRowsSelect) {
        onRowsSelect(list.map((item) => item.original.id));
      }
    },
    [rows],
  );

  const onExpandClick = (id: string, rowId?: string) => {
    if (onExpand && rowId && !expandedRows.includes(rowId)) {
      onExpand(rowId);
    }

    if (expandedRows.find((item) => item === rowId)) {
      setExpandedRows([]);
    } else {
      setExpandedRows([rowId as string]);
    }
  };

  const onSaveFilter = () => {
    let nextSavedFilters: {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[] = [];

    const savedFiltersJSON = localStorage.getItem(savedFiltersKey as string);

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        nextSavedFilters = parsed;

        nextSavedFilters.push({
          id: `${savedFiltersKey}-${(nextSavedFilters?.length || 0) + 1}`,
          name: filterName,
          value: filters,
          employeeId: userId as string,
        });

        nextSavedFilters = nextSavedFilters?.map((savedFilter) => {
          const result = { ...savedFilter };

          if (result.employeeId === userId) {
            if (defaultFilter) {
              if (
                savedFilter.id ===
                `${savedFiltersKey}-${nextSavedFilters?.length}`
              ) {
                result.default = defaultFilter;
              } else {
                result.default = false;
              }
            }
          }

          return result;
        });
      }
    } else {
      nextSavedFilters.push({
        id: `${savedFiltersKey}-${(nextSavedFilters?.length || 0) + 1}`,
        name: filterName,
        value: filters,
        default: defaultFilter,
        employeeId: userId as string,
      });
    }

    localStorage.setItem(
      savedFiltersKey as string,
      JSON.stringify(nextSavedFilters),
    );

    if (setSavedFilters) {
      setSavedFilters(nextSavedFilters);
    }

    if (setSelectedSavedFilter) {
      setSelectedSavedFilter(`${savedFiltersKey}-${nextSavedFilters?.length}`);
    }

    setFilterName('');
    setDefaultFilter(false);

    setSaveFilterPopupVisible(false);
  };

  const onUploadFiltersPreset = (files: FileList) => {
    setUploadErrorTextVisible(false);

    const file = files[0];

    const reader = new FileReader();

    reader.onload = (evt) => {
      const fileContent = evt.target?.result;

      if (fileContent && typeof fileContent === 'string') {
        try {
          const parsed = JSON.parse(fileContent);

          if (parsed) {
            if (parsed.key === savedFiltersKey && setSavedFilters) {
              let nextSavedFilters;

              const currentSavedFiltersJSON = localStorage.getItem(
                savedFiltersKey as string,
              );

              if (currentSavedFiltersJSON) {
                const parsedCurrentSavedFilters = JSON.parse(
                  currentSavedFiltersJSON,
                );

                nextSavedFilters = parsedCurrentSavedFilters;

                parsed.values?.forEach((filterValue: any) => {
                  nextSavedFilters.push({
                    ...filterValue,
                    employeeId: userId,
                    default: false,
                  });
                });
              } else {
                nextSavedFilters = parsed.values?.map(
                  (filterItem: any, index: number) => ({
                    ...filterItem,
                    id: `${savedFiltersKey}-${index + 1}`,
                    employeeId: userId,
                    default: false,
                  }),
                );
              }

              nextSavedFilters = nextSavedFilters?.map(
                (filterItem: any, index: number) => ({
                  ...filterItem,
                  id: `${savedFiltersKey}-${index + 1}`,
                }),
              );

              setSavedFilters(
                nextSavedFilters?.filter(
                  (filterItem: any) => filterItem.employeeId === userId,
                ),
              );

              setUploadFilterSuccess(true);
              setUploadedFileName(file.name);

              localStorage.setItem(
                savedFiltersKey as string,
                JSON.stringify(nextSavedFilters),
              );

              if (uploadFiltersPresetRef?.current) {
                uploadFiltersPresetRef.current.value = '';
              }

              if (reuploadFiltersPresetRef?.current) {
                reuploadFiltersPresetRef.current.value = '';
              }
            } else {
              setUploadErrorTextVisible(true);
              setUploadFilterSuccess(false);

              if (uploadFiltersPresetRef?.current) {
                uploadFiltersPresetRef.current.value = '';
              }

              if (reuploadFiltersPresetRef?.current) {
                reuploadFiltersPresetRef.current.value = '';
              }
            }
          }
        } catch {
          setUploadErrorTextVisible(true);
          setUploadFilterSuccess(false);

          if (uploadFiltersPresetRef?.current) {
            uploadFiltersPresetRef.current.value = '';
          }

          if (reuploadFiltersPresetRef?.current) {
            reuploadFiltersPresetRef.current.value = '';
          }
        }
      }
    };

    reader.readAsText(file);
  };

  useEffect(() => {
    if (titleRef?.current) {
      setTimeout(() => {
        setTopContentHeight(titleRef?.current?.clientHeight as number);
      }, 1);
    }
    // eslint-disable-next-line react-hooks/refs
  }, [titleRef?.current?.clientHeight, filters, hiddedAllFilterTags]);

  // useEffect(() => {
  //   if (onRowsSelect && !withSelectAllRowsOnExpand) {
  //     onRowsSelect(selectedRows.map((item) => item.original.id));
  //   }
  // }, [selectedRows]);

  useEffect(() => {
    if (isLoading) {
      setExpandedRows([]);
    }
  }, [isLoading]);

  useEffect(() => {
    if (collapseAllRows) {
      setExpandedRows([]);
    }
  }, [collapseAllRows]);

  useEffect(() => {
    if (uploadFilterSuccess) {
      setTimeout(() => {
        setFilterName('');
        setUploadFilterSuccess(false);
      }, 10000);
    }
  }, [uploadFilterSuccess]);

  useEffect(() => {
    if (withSelectAllRowsOnExpand) {
      toggleAll(true);
    }
  }, [withSelectAllRowsOnExpand]);

  useEffect(() => {
    if (prevRows && rows && !isEqual(prevRows, rows) && !withSaveSelectedRows) {
      const nextSelectedRows: Row[] = [];

      selectedRows.forEach((item) => {
        if (
          (rows as Row[]).find((row) => row.original.id === item.original.id)
        ) {
          nextSelectedRows.push(item);
        }
      });

      setSelectedRows(nextSelectedRows);
    }
  }, [selectedRows, rows, prevRows, withSaveSelectedRows]);

  useEffect(() => {
    if (allSelected) {
      if (
        disabledRowIds &&
        !withSaveSelectedRows &&
        selectedRows.length !==
          rows?.filter((item) =>
            disabledRowIds?.every((id) => item.original.id !== id),
          )?.length
      ) {
        setAllSelected(false);
      } else if (!disabledRowIds && selectedRows.length !== rows.length) {
        setAllSelected(false);
      }
    } else if (!allSelected) {
      if (!disabledRowIds && selectedRows.length === rows.length) {
        setAllSelected(true);
      } else if (
        disabledRowIds &&
        selectedRows.length ===
          rows?.filter((item) =>
            disabledRowIds?.every((id) => item.original.id !== id),
          )?.length
      ) {
        setAllSelected(true);
      }
    }
  }, [allSelected, selectedRows, rows, withSaveSelectedRows]);

  useEffect(() => {
    if (clearSelectedItems) {
      setSelectedRows([]);
      setAllSelected(false);

      if (onRowsSelect) {
        onRowsSelect([]);
      }
    }
  }, [clearSelectedItems]);

  useEffect(() => {
    if (selectedIds && rows?.length) {
      const nextSelectedRows: Row[] = [];

      rows.forEach((item) => {
        if (selectedIds?.find((id) => item.original.id === id)) {
          nextSelectedRows.push(item as Row);
        }
      });

      setSelectedRows(nextSelectedRows);
    }
  }, [selectedIds, rows]);

  useEffect(() => {
    if (prevColumns && !isEqual(columns, prevColumns)) {
      setTableColumns(columns);
    }
  }, [prevColumns, columns]);

  useEffect(() => {
    if (
      expandable &&
      expandedRows?.length &&
      isSuccessExpandableRow &&
      !isLoadingExpandableRow &&
      tableContainerRef &&
      tableContainerRef.current
    ) {
      const currentOriginalRow = rows?.find(
        (item) => item.original.id === expandedRows[0],
      );

      if (currentOriginalRow && currentOriginalRow?.original?.values?.length) {
        const currentRow = document.getElementById(expandedRows[0]);

        setTimeout(() => {
          if (currentRow && tableContainerRef && tableContainerRef.current) {
            // @ts-ignore
            const rect = currentRow.nextSibling.getBoundingClientRect();

            const isVisible =
              rect.bottom <= tableContainerRef?.current?.clientHeight;

            if (!isVisible) {
              // @ts-ignore
              currentRow.nextSibling.scrollIntoView({
                block: 'end',
              });
            }
          }
        }, 300);
      }
    }
  }, [
    expandedRows,
    isSuccessExpandableRow,
    isLoadingExpandableRow,
    tableContainerRef,
    expandable,
    rows,
  ]);

  const elementIsVisibleInViewport = (
    el: any,
    index: number,
    partiallyVisible = false,
  ) => {
    const { top, left, right } = el.getBoundingClientRect();
    const { offsetWidth } = tableContainerRef.current as HTMLDivElement;

    const visible = partiallyVisible
      ? (left > 0 && left < offsetWidth) || (right > 0 && right < offsetWidth)
      : top >= 0 && left >= 0 && right <= offsetWidth;

    if (visible) {
      let currentIndex = index;

      if (currentIndex < 9) {
        currentIndex = tableColumns.filter((item) => !item.hidden).length;
      }

      if (index > 11) {
        setExpandableColspan(currentIndex - 1);
      } else {
        setExpandableColspan(currentIndex);
      }
    }
  };

  useEffect(() => {
    if (
      prevFetching &&
      !isFetching &&
      withVerticalScroll &&
      tableContainerRef &&
      tableContainerRef.current
    ) {
      tableContainerRef.current.scrollTo(0, 0);
    }
  }, [isFetching, prevFetching, withVerticalScroll, tableContainerRef]);

  useEffect(() => {
    if (disabledRowIds && disabledRowIds?.length && selectedRows?.length) {
      const nextSelectedRows: Row[] = [];

      selectedRows?.forEach((item) => {
        if (!disabledRowIds?.find((id) => item.original.id === id)) {
          nextSelectedRows.push(item);
        }
      });

      setSelectedRows(nextSelectedRows);
    }
  }, [disabledRowIds]);

  const tableContent = (
    <div
      ref={tableContainerRef}
      className={`table-container ${
        withScroll ? 'table-container-with-scroll' : ''
      }`}

      {...wheelHandlers}
      onScroll={(e) => {
        if (withScroll) {
          onWheel(e.currentTarget.scrollLeft);

          if (falseScrollRightRef.current && onWheelActive) {
            falseScrollRightRef.current.scroll({
              top: e.currentTarget.scrollTop,
            });
          }
        }
      }}
    >
      <div className={`table-top ${tableTopClassName}`} ref={titleRef}>
        {title && <h3 className="table-title">{title}</h3>}
        <div className="table-top-content-wrapper">
          {uploadErrorTextVisible && (
            <div className="uploaded-error">
              The uploaded file is invalid. Please re-download the file with
              filters and try to upload them again.
              <button
                type="button"
                className="close-uploaded-error"
                aria-label="Close"
                onClick={() => setUploadErrorTextVisible(false)}
              >
                <IconClose width={14} height={14} />
              </button>
            </div>
          )}

          {(topContent || withSettings) && (
            <div className={`table-controls ${tableControlsClassName}`}>
              {topContent && (
                <div className="table-top-content">{topContent}</div>
              )}
              {withSettings && (
                <TableSettings
                  headers={tableColumns}
                  storeKey={storeKey as string}
                  onChange={setTableColumns}
                />
              )}
            </div>
          )}
          <div className="table-top-filters">
            {filtersContent}
            {filtersContent && <div className="table-top-filters-separator" />}
            {savedFiltersKey && (
              <SavedFilters
                savedFiltersKey={savedFiltersKey}
                savedFilters={savedFilters}
                setSavedFilters={setSavedFilters}
                userId={userId}
                selectedSavedFilter={selectedSavedFilter}
                onSavedFilterClick={onSavedFilterClick}
                onFilterChange={onFilterChange}
                filters={filters}
                setSelectedSavedFilter={setSelectedSavedFilter}
                columnsLabels={columnsLabels}
                filtersComponent={filtersComponent}
                setSaveFilterPopupVisible={setSaveFilterPopupVisible}
                onUploadFiltersPreset={onUploadFiltersPreset}
                uploadFiltersPresetRef={uploadFiltersPresetRef}
              />
            )}
          </div>
        </div>

        {selectedOptionsTags.length && !hiddedAllFilterTags ? (
          <TagsList
            tags={selectedOptionsTags}
            lists={filtersComponent?.list}
            filters={filters}
            foundedEntriesForTags={pagination?.totalCount}
            onFilterChange={onFilterChange}
            onClearAutoComplete={onClearAutoComplete}
            hiddenFiltersTags={hiddenFiltersTags}
            filtersLists={filtersLists}
            setFiltersList={setFiltersList}
          />
        ) : null}
      </div>
      <table className={`table-content ${tableClassName}`} ref={tableRef}>
        <thead
          style={{ top: `${topContentHeight as number}px` }}
          className={tableTheadClassName}
        >
          <tr className="table-head-row">
            {expandable && (
              <th className="expandable-header" aria-label="Expanded header">
                <div className={`table-head-cell ${tableHeadCellClassName}`} />
              </th>
            )}

            {Boolean(onRowsSelect) && (
              <th className="table-head-checkbox" aria-label="Head checkbox">
                <div className={`table-head-cell ${tableHeadCellClassName}`}>
                  {withToggleInput ? (
                    <ToggleInput
                      name={tableName ? `checkbox-${tableName}` : 'checbox'}
                      title=""
                      checked={
                        disabledRowIds
                          ? Boolean(
                              withSaveSelectedRows
                                ? rows
                                    ?.filter((item) =>
                                      disabledRowIds.every(
                                        (id) => id !== item?.original?.id,
                                      ),
                                    )
                                    ?.map((item) => item.original.id)
                                    .every((item) =>
                                      selectedRows
                                        .map((rowItem) => rowItem.original.id)
                                        .includes(item),
                                    )
                                : Boolean(
                                    rows?.filter((item) =>
                                      disabledRowIds.every(
                                        (id) => id !== item?.original?.id,
                                      ),
                                    ).length > 0 &&
                                    rows?.filter((item) =>
                                      disabledRowIds.every(
                                        (id) => id !== item?.original?.id,
                                      ),
                                    ).length === selectedRows.length &&
                                    selectedRows.length > 0 &&
                                    allSelected,
                                  ),
                            )
                          : Boolean(rows.length && allSelected)
                      }
                      onChange={() => {
                        if (!withSaveSelectedRows) {
                          toggleAll(!allSelected);

                          if (onToggleAll) {
                            onToggleAll(!allSelected);
                          }
                        } else {
                          toggleAllWithSaveSelected();
                        }
                      }}
                      disabled={Boolean(
                        !rows.length ||
                        disabledRowIds?.length === rows.length ||
                        rowsSelectDisabled,
                      )}
                    />
                  ) : (
                    <CheckboxInput
                      type="checkbox"
                      name={tableName ? `checkbox-${tableName}` : 'checbox'}
                      title=""
                      checked={
                        disabledRowIds
                          ? Boolean(
                              withSaveSelectedRows
                                ? rows
                                    ?.filter((item) =>
                                      disabledRowIds.every(
                                        (id) => id !== item?.original?.id,
                                      ),
                                    )
                                    ?.map((item) => item.original.id)
                                    .every((item) =>
                                      selectedRows
                                        .map((rowItem) => rowItem.original.id)
                                        .includes(item),
                                    )
                                : rows?.filter((item) =>
                                    disabledRowIds.every(
                                      (id) => id !== item?.original?.id,
                                    ),
                                  ).length === selectedRows.length &&
                                    selectedRows.length > 0 &&
                                    allSelected,
                            )
                          : Boolean(rows.length && allSelected)
                      }
                      onChange={() =>
                        !withSaveSelectedRows
                          ? toggleAll(!allSelected)
                          : toggleAllWithSaveSelected()
                      }
                      disabled={Boolean(
                        !rows.length ||
                        disabledRowIds?.length === rows.length ||
                        rowsSelectDisabled,
                      )}
                    />
                  )}
                </div>
              </th>
            )}
            {tableColumns
              .filter((item) => !item.hidden)
              .map((column, index) => (
                <TableHeadItem
                  key={column.name}
                  morePadding={Boolean(
                    index === 0 &&
                    transactionTable &&
                    rows?.find((row) => row.original.deletedAt) &&
                    !expandable &&
                    !onRowsSelect,
                  )}
                  withScroll={withScroll}
                  tableHeadCellClassName={tableHeadCellClassName}
                  filtersComponent={filtersComponent}
                  sort={sort}
                  fullWidthTable={fullWidthTable}
                  onSort={onSort}
                  filters={filters}
                  column={column}
                  elementIsVisibleInViewport={elementIsVisibleInViewport}
                  index={index}
                />
              ))}
          </tr>
        </thead>
        <tbody
          className={`table-body ${tableBodyClassName} ${
            withScroll ? 'with-scroll' : ''
          }`}
        >
          {isLoading && (
            <tr className="table-loader-wrapper">
              <td className="table-loader" aria-label="Table loader">
                <IconLoader />
              </td>
            </tr>
          )}
          {!isLoading && !rows.length && !error && (
            <tr className="table-empty-message">
              <td>Data not found</td>
            </tr>
          )}
          {error && (
            <tr className="table-error">
              <td>
                <IconAttention />
                <div className="table-error-title">Unable to load data</div>
                <div className="table-error-message">{error}</div>
                <div>
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    className="reload-table-button"
                    text="Reload"
                    icon={<IconRefresh />}
                    onClick={() => {
                      onFilterChange({
                        cursor: filters.cursor,
                      });

                      setFiltersList(undefined);

                      if (onClearAutoComplete) {
                        onClearAutoComplete();
                      }
                    }}
                  />
                </div>
              </td>
            </tr>
          )}
          {rows.map((row, index) => (
            <TableRow
              key={row.original.id}
              row={row}
              hasDeleted={Boolean(
                transactionTable &&
                rows?.find((rowItem) => rowItem.original.deletedAt),
              )}
              isLoading={isLoading}
              onRowsSelect={onRowsSelect}
              onRowClick={onRowClick}
              selectRow={selectRow}
              expandable={expandable}
              onExpand={onExpandClick}
              selected={Boolean(
                selectedRows.find(
                  (item) => item.original.id === row.original.id,
                ),
              )}
              expandedRows={expandedRows}
              columns={tableColumns.filter((item) => !item.hidden)}
              expandableColumns={expandableColumns}
              data={data[index]}
              isLoadingExpandableRow={isLoadingExpandableRow}
              expandableColspan={expandableColspan}
              disabledRowIds={disabledRowIds}
              clickable={clickable}
              linkWithoutSearch={linkWithoutSearch}
              withDuplicateButton={withDuplicateButton}
              onDuplicateClick={onDuplicateClick}
              transactionTable={transactionTable}
              withExpandableContent={withExpandableContent}
              expandableColspanValue={expandableColspanValue}
              withToggleInput={withToggleInput}
              tableName={tableName}
              newWindow={newWindow}
              rowsSelectDisabled={rowsSelectDisabled}
              withDeleteIcon={withDeleteIcon}
            />
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {nestedTable ? (
        <div className={`table-form ${tableFormClassName}`}>{tableContent}</div>
      ) : (
        <form
          onSubmit={
            handleSubmit && handleSubmit((values) => onFilterChange(values))
          }
          className={`table-form ${tableFormClassName}`}
        >
          {tableContent}
        </form>
      )}

      {withScroll && (
        <>
          <TableHorizontalScroll
            tableScrollWrapper={tableScrollWrapper}
            tableScrollerRef={tableScrollerRef}
            onScrollButton={onScrollButton}
            scrollThumbPosition={scrollThumbPosition}
            onDrag={onDrag}
            onDragEnd={onDragEnd}
            accessibleDragPosition={accessibleDragPosition}
            scrollBarThumbWidth={scrollBarThumbWidth}
          />
          <div
            ref={falseScrollRightRef}
            className={`false-scroll-right ${!pagination ? 'more-height' : ''}`}
            onScroll={(e) => {
              if (tableContainerRef.current && onWheelActive) {
                tableContainerRef.current.scroll({
                  top: e.currentTarget.scrollTop,
                });
              }
            }}
          >
            <div
              className="false-scroll-right-content"
              style={{
                // eslint-disable-next-line react-hooks/refs
                height: `${tableRef?.current?.offsetHeight as number}px`,
              }}
            />
          </div>
        </>
      )}
      {pagination && (
        <div className={`pagination ${withScroll ? 'without-border' : ''}`}>
          <div className="pagination-buttons">
            <ButtonPseudo
              text="Prev"
              icon={<IconLeft />}
              size={ButtonSize.SMALL}
              className="button-white pagination-button"
              disabled={!pagination?.hasPreviousPage || paginationDisabled}
              onClick={() => {
                if (setPage && take) {
                  setPage({
                    id: pagination?.startCursor,
                    take: `-${take}`,
                  });
                }
              }}
              tabIndex={-1}
            />
            <ButtonPseudo
              text="Next"
              icon={<IconRight />}
              size={ButtonSize.SMALL}
              className="pagination-button button-next button-white"
              disabled={!pagination?.hasNextPage || paginationDisabled}
              onClick={() => {
                if (setPage && take) {
                  setPage({
                    id: pagination?.endCursor,
                    take,
                  });
                }
              }}
              tabIndex={-1}
            />
          </div>
        </div>
      )}
      <Popup
        title="Save current filter"
        description=""
        onCancelClick={() => {
          setSaveFilterPopupVisible(false);
          setFilterName('');
        }}
        onOkClick={() => onSaveFilter()}
        cancelButtonText="Close"
        okButtonText="Save"
        isVisible={saveFilterPopupVisible}
        okButtonDisabled={!filterName}
      >
        <div>
          <InputText
            name="filterName"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="Enter filter name"
            labelClassName="filter-name-input"
          />
          <CheckboxInput
            type="checkbox"
            name="defaultFilter"
            checked={defaultFilter}
            onChange={(e) => setDefaultFilter(e.target.checked)}
            title="Save as default"
          />
        </div>
      </Popup>
      {uploadErrorTextVisible && (
        <div className="uploaded-error">
          <div className="uploaded-error-left">
            <div className="uploaded-error-icon-wrapper">
              <IconBlock width={20} />
            </div>
            <div className="upload-error-text">
              The uploaded file is invalid. Please re-download the file with
              filters and try <br /> to upload them again.
            </div>
          </div>
          <div className="uploaded-error-right">
            <div className="uploaded-error-right-input-wrapper">
              <ButtonSecondary
                size={ButtonSize.SMALL}
                text="Select file"
                className="button-white upload-file-button"
                icon={<IconUpload />}
                onClick={() => reuploadFiltersPresetRef?.current?.click()}
              />
              <input
                type="file"
                className="upload-input"
                ref={reuploadFiltersPresetRef}
                onChange={(e) =>
                  onUploadFiltersPreset(e.target.files as FileList)
                }
                accept=".txt"
              />
            </div>
            <div className="upload-error-close-button">
              <ButtonSecondary
                size={ButtonSize.SMALL}
                text="Close"
                className="button-white upload-file-button"
                onClick={() => setUploadErrorTextVisible(false)}
              />
            </div>
          </div>
        </div>
      )}
      {uploadFilterSuccess && (
        <div className="uploaded-file-success">
          <div className="uploaded-file-success-left">
            <div className="uploaded-file-success-icon-wrapper">
              <IconSelected width={20} />
            </div>
            <div className="uploaded-file-success-text">{`Preset "${uploadedFileName}" added`}</div>
          </div>
          <div className="uploaded-file-success-close-wrapper">
            <ButtonSecondary
              size={ButtonSize.SMALL}
              text="Close"
              className="button-white close-button"
              onClick={() => {
                setUploadFilterSuccess(false);
                setFilterName('');
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
